import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import config from '../../../../../config';
import {
  currencyFormatter, formatDecimalToFixedTwoPercent,
} from '../../../../../shared/components/table/functions';
import { doFetch } from '../../../../../helpers';
import PpcReportGrid from './PpcReportGrid';
import { isAdRev } from '../../../../../shared/components/domainSupport';

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

const columnDefs = [
  {
    field: 'sku',
    headerName: 'SKU',
    rowGroup: true,
    enableRowGroup: true,
  },
  {
    field: 'asin',
    headerName: 'ASIN',
  },
  {
    field: 'brand',
    headerName: 'Brand',
  },
  {
    field: 'title',
    headerName: 'Product Title/Count',
  },
  {
    field: 'number_of_campaigns',
    headerName: '# Of Campaigns',
  },
  {
    field: 'impressions',
    headerName: 'Impressions',
  },
  {
    field: 'clicks',
    headerName: 'Clicks',
  },
  {
    field: 'spend',
    headerName: 'PPC Spend',
    valueFormatter: currencyFormatter,
  },
  {
    field: 'sales',
    headerName: 'PPC Sales',
    valueFormatter: currencyFormatter,
  },
  {
    field: 'orders',
    headerName: 'Orders',
  },
  {
    field: 'units',
    headerName: 'Units',
  },
  // {
  //   field: 'aov',
  //   headerName: 'AOV',
  //   valueFormatter: currencyFormatter,
  //   headerTooltip: 'Average Order Value',
  // },
  {
    field: 'acos',
    headerName: 'PPC ACoS',
    valueFormatter: formatDecimalToFixedTwoPercent,
  },
  {
    field: 'ctr',
    headerName: 'CTR',
    valueFormatter: formatDecimalToFixedTwoPercent,
    headerTooltip: 'Click-through rate',
  },
  {
    field: 'cpc',
    headerName: 'CPC',
    valueFormatter: currencyFormatter,
    headerTooltip: 'Cost per-click',
  },
  {
    field: 'cr',
    headerName: 'CR',
    valueFormatter: formatDecimalToFixedTwoPercent,
    headerTooltip: 'Conversion rate',
  },
  {
    field: 'roas',
    headerName: 'PPC ROAS',
    valueFormatter: currencyFormatter,
  },
  {
    field: 'spot',
    headerName: 'Spend % of total',
    valueFormatter: formatDecimalToFixedTwoPercent,
    sortable: false,
  },
  {
    field: 'segment_one',
    headerName: 'Segment 1',
  },
  {
    field: 'segment_two',
    headerName: 'Segment 2',
  },
  {
    field: 'segment_three',
    headerName: 'Segment 3',
  },
  {
    field: 'segment_four',
    headerName: 'Segment 4',
  },
  {
    field: 'segment_five',
    headerName: 'Segment 5',
  },
];

const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  aggFunc: 'none',
  cellStyle: (params) => {
    if (params.value < 0.00) {
      return { color: 'red' };
    }
    return null;
  },
};

const autoGroupColumnDef = {
  headerName: ' Breakdown ',
  cellRendererParams: {
    suppressCount: true,
  },
};

const pivotButtons = [
  { buttonName: 'SKU', buttonAction: 'sku' },
  { buttonName: 'Brand', buttonAction: 'brand' },
  { buttonName: 'Segment 1', buttonAction: 'segment_one' },
  { buttonName: 'Segment 2', buttonAction: 'segment_two' },
  { buttonName: 'Segment 3', buttonAction: 'segment_three' },
  { buttonName: 'Segment 4', buttonAction: 'segment_four' },
  { buttonName: 'Segment 5', buttonAction: 'segment_five' },
];

function createServer({
  sellerSelect, user, startDate, endDate, pageSize,
}) {
  return {
    getData: async (request) => {
      const apiEndPoint = `${apiUrl}/sellers/ppc-report-paginated`;
      const { startRow, sortModel, rowGroupCols } = request;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
      const requestOptions = {
        method: 'GET',
        headers: headerWithAuth,
      };
      const url = `${apiEndPoint}?sellerAccountId=${sellerSelect.value.toString()}&accountType=${sellerSelect.type}&startDate=${moment(startDate).format('YYYYMMDD')}&endDate=${moment(endDate).format('YYYYMMDD')}&limit=${pageSize}&offset=${startRow}&sort=${encodeURIComponent(JSON.stringify(sortModel))}&grouping=${encodeURIComponent(JSON.stringify(rowGroupCols))}`;
      const data = await doFetch(url, requestOptions);
      return data;
    },
  };
}

function createDatasource(server) {
  return {
    getRows: async (params) => {
      const {
        request, successCallback: success, failCallback: fail,
      } = params;
      try {
        const response = await server.getData(request);
        success(response.results, response.rowCount);
      } catch (error) {
        fail();
      }
    },
  };
}

export default function SellerPpcReportMain() {
  const user = useSelector(state => state.authentication.user);
  const sellerSelect = useSelector(state => state.sellerSelect);
  const [reportStartDate, setReportStartDate] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [reportEndDate, setReportEndDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const paginationPageSize = 10000;
  const server = createServer({
    sellerSelect, user, startDate: reportStartDate, endDate: reportEndDate, pageSize: paginationPageSize,
  });
  const dataSource = createDatasource(server);

  function onDateChange(startDate, endDate) {
    setReportStartDate(moment(startDate).format('YYYY-MM-DD'));
    setReportEndDate(moment(endDate).format('YYYY-MM-DD'));
  }

  if (isAdRev() && pivotButtons.length > 2) {
    columnDefs.splice(columnDefs.length - 5, 5);
    pivotButtons.splice(pivotButtons.length - 5, 5);
  }

  return (
    <PpcReportGrid
      gridInfo={{
        columnDefs,
        defaultColDef,
        autoGroupColumnDef,
        dataSource,
        paginationPageSize,
      }}
      datePickerInfo={{
        inputStart: moment(reportStartDate).toDate(),
        inputEnd: moment(reportEndDate).toDate(),
        onChange: onDateChange,
      }}
      pivotButtons={pivotButtons}
    />
  );
}
